/**
 * Fase 29 · Módulo de Facturación (Master + Admin general).
 *
 * Sub-secciones (Tabs):
 *  - Tarifas: CRUD por servicio (globales + overrides por empresa).
 *  - Planes: CRUD de paquetes que se publican en la landing.
 *  - Facturas: seleccionar empresa+período → generar PDF → descargar/enviar.
 *  - Config: contador correlativo (solo Master).
 */
import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Receipt, Plus, Loader2, Download, Send, Trash2, Pencil, RefreshCw,
  DollarSign, Package, Settings, FileText,
} from "lucide-react";
import { toast } from "sonner";

const SERVICE_TYPES = [
  { key: "ai_credit", label: "Crédito de IA" },
  { key: "social_post", label: "Publicación en redes sociales" },
  { key: "ml_post", label: "Publicación en MercadoLibre" },
  { key: "email_sent", label: "Correo enviado" },
  { key: "monthly_fixed", label: "Servicios mensuales fijos" },
];

export default function Billing() {
  const { user } = useAuth();
  if (!user || (user.role !== "master" && user.role !== "admin")) {
    return (
      <div className="p-8 text-center text-stone-500" data-testid="billing-forbidden">
        Solo el Master y el Admin general pueden acceder al módulo de Facturación.
      </div>
    );
  }
  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="billing-page">
      <div>
        <div className="divider-gold mb-3" />
        <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
          <Receipt className="h-8 w-8 text-brand-emerald" /> Facturación
        </h1>
        <p className="text-sm text-stone-500 mt-2 max-w-2xl">
          Define tarifas, planes visibles en la landing pública y emite facturas por empresa.
        </p>
      </div>

      <Tabs defaultValue="facturas">
        <TabsList data-testid="billing-tabs">
          <TabsTrigger value="facturas" data-testid="tab-invoices"><FileText className="h-4 w-4 mr-1" /> Facturas</TabsTrigger>
          <TabsTrigger value="tarifas" data-testid="tab-rates"><DollarSign className="h-4 w-4 mr-1" /> Tarifas</TabsTrigger>
          <TabsTrigger value="planes" data-testid="tab-plans"><Package className="h-4 w-4 mr-1" /> Planes</TabsTrigger>
          {user.role === "master" && (
            <TabsTrigger value="config" data-testid="tab-config"><Settings className="h-4 w-4 mr-1" /> Configuración</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="facturas" className="mt-6"><InvoicesPanel /></TabsContent>
        <TabsContent value="tarifas" className="mt-6"><RatesPanel /></TabsContent>
        <TabsContent value="planes" className="mt-6"><PlansPanel /></TabsContent>
        {user.role === "master" && (
          <TabsContent value="config" className="mt-6"><ConfigPanel /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ---------------- Panel Tarifas ----------------
function RatesPanel() {
  const [rates, setRates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get("/billing/rates"),
        api.get("/companies/").catch(() => ({ data: [] })),
      ]);
      setRates(r.data || []);
      setCompanies(c.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ service_type: "ai_credit", label: "", price_usd: 0, company_id: null }); setDialogOpen(true); };
  const openEdit = (r) => { setEditing({ ...r }); setDialogOpen(true); };
  const save = async () => {
    try {
      if (editing.id) {
        await api.patch(`/billing/rates/${editing.id}`, { label: editing.label, price_usd: parseFloat(editing.price_usd) });
      } else {
        await api.post("/billing/rates", {
          service_type: editing.service_type,
          label: editing.label || null,
          price_usd: parseFloat(editing.price_usd),
          company_id: editing.company_id || null,
        });
      }
      toast.success("Tarifa guardada");
      setDialogOpen(false); setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta tarifa?")) return;
    try {
      await api.delete(`/billing/rates/${id}`);
      toast.success("Tarifa eliminada");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-stone-500">
          Las tarifas sin empresa asignada son <strong>globales</strong>. Se sobrescriben por tarifa específica de la empresa.
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
          <Button className="btn-primary" onClick={openNew} data-testid="new-rate-btn"><Plus className="h-4 w-4 mr-1" /> Nueva tarifa</Button>
        </div>
      </div>
      <div className="card-elevated overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-stone-50/60">
            <TableHead>Servicio</TableHead>
            <TableHead>Etiqueta</TableHead>
            <TableHead>Empresa</TableHead>
            <TableHead className="text-right">Precio USD</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></TableCell></TableRow>
            ) : rates.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-stone-500">Sin tarifas. Crea una para comenzar.</TableCell></TableRow>
            ) : (
              rates.map((r) => (
                <TableRow key={r.id} data-testid={`rate-row-${r.id}`}>
                  <TableCell><Badge className="bg-brand-emerald-soft text-brand-emerald-dark border border-brand-emerald/20">{SERVICE_TYPES.find((s) => s.key === r.service_type)?.label || r.service_type}</Badge></TableCell>
                  <TableCell>{r.label}</TableCell>
                  <TableCell>{r.company_id ? (companies.find((c) => c.id === r.company_id)?.name || r.company_id.slice(0, 8)) : <span className="text-stone-400 italic">Global</span>}</TableCell>
                  <TableCell className="text-right font-mono">${Number(r.price_usd).toFixed(2)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-rate-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(r.id)} className="text-destructive" data-testid={`delete-rate-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!v) { setDialogOpen(false); setEditing(null); } }}>
        <DialogContent data-testid="rate-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Editar tarifa" : "Nueva tarifa"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              {!editing.id && (
                <div className="space-y-1">
                  <Label>Servicio</Label>
                  <Select value={editing.service_type} onValueChange={(v) => setEditing({ ...editing, service_type: v })}>
                    <SelectTrigger data-testid="rate-service-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map((s) => (<SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1">
                <Label>Etiqueta visible (opcional)</Label>
                <Input value={editing.label || ""} onChange={(e) => setEditing({ ...editing, label: e.target.value })} data-testid="rate-label" />
              </div>
              <div className="space-y-1">
                <Label>Precio unitario (USD)</Label>
                <Input type="number" step="0.01" min="0" value={editing.price_usd} onChange={(e) => setEditing({ ...editing, price_usd: e.target.value })} data-testid="rate-price" />
              </div>
              {!editing.id && (
                <div className="space-y-1">
                  <Label>Empresa (opcional — vacío = global)</Label>
                  <Select value={editing.company_id || "__global__"} onValueChange={(v) => setEditing({ ...editing, company_id: v === "__global__" ? null : v })}>
                    <SelectTrigger data-testid="rate-company"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__global__">Global (default)</SelectItem>
                      {companies.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditing(null); }}>Cancelar</Button>
            <Button className="btn-primary" onClick={save} data-testid="rate-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Panel Planes ----------------
function PlansPanel() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => { setLoading(true); try { const { data } = await api.get("/billing/plans"); setPlans(data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ name: "", description: "", price_usd: 0, features: [], active: true, published_on_landing: true }); setDialogOpen(true); };
  const openEdit = (p) => { setEditing({ ...p, features_str: (p.features || []).join("\n") }); setDialogOpen(true); };
  const save = async () => {
    try {
      const features = (editing.features_str || "").split("\n").map((f) => f.trim()).filter(Boolean);
      const payload = {
        name: editing.name, description: editing.description || null,
        price_usd: parseFloat(editing.price_usd),
        features, active: !!editing.active, published_on_landing: !!editing.published_on_landing,
      };
      if (editing.id) await api.patch(`/billing/plans/${editing.id}`, payload);
      else await api.post("/billing/plans", payload);
      toast.success("Plan guardado");
      setDialogOpen(false); setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este plan?")) return;
    try { await api.delete(`/billing/plans/${id}`); toast.success("Plan eliminado"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-stone-500">Los planes marcados como "publicado en landing" aparecen en la página principal como promociones.</div>
        <Button className="btn-primary" onClick={openNew} data-testid="new-plan-btn"><Plus className="h-4 w-4 mr-1" /> Nuevo plan</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-3 py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>
        ) : plans.length === 0 ? (
          <div className="col-span-3 py-12 text-center text-stone-500">Sin planes. Crea el primero.</div>
        ) : (
          plans.map((p) => (
            <div key={p.id} className="card-elevated p-5 flex flex-col" data-testid={`plan-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div className="font-serif-display text-2xl text-brand-ink">{p.name}</div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(p.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="text-3xl font-bold text-brand-emerald mt-2">USD {Number(p.price_usd).toFixed(2)}</div>
              <p className="text-sm text-stone-500 mt-2 mb-3">{p.description}</p>
              <ul className="space-y-1 text-sm flex-1">
                {(p.features || []).map((f, i) => (<li key={i} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-gold" /> {f}</li>))}
              </ul>
              <div className="flex gap-2 mt-3">
                {p.active && <Badge className="bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/30">Activo</Badge>}
                {p.published_on_landing && <Badge className="bg-brand-gold-soft text-brand-gold-dark border-brand-gold/40">En landing</Badge>}
              </div>
            </div>
          ))
        )}
      </div>
      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!v) { setDialogOpen(false); setEditing(null); } }}>
        <DialogContent data-testid="plan-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Editar plan" : "Nuevo plan"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Nombre</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="plan-name" /></div>
              <div><Label>Descripción</Label><Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="plan-description" /></div>
              <div><Label>Precio (USD)</Label><Input type="number" step="0.01" min="0" value={editing.price_usd} onChange={(e) => setEditing({ ...editing, price_usd: e.target.value })} data-testid="plan-price" /></div>
              <div><Label>Características (una por línea)</Label>
                <Textarea rows={5} value={editing.features_str || (editing.features || []).join("\n")} onChange={(e) => setEditing({ ...editing, features_str: e.target.value })} data-testid="plan-features" />
              </div>
              <label className="flex items-center gap-2"><Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="plan-active" /> Activo</label>
              <label className="flex items-center gap-2"><Switch checked={editing.published_on_landing} onCheckedChange={(v) => setEditing({ ...editing, published_on_landing: v })} data-testid="plan-landing" /> Publicado en landing</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditing(null); }}>Cancelar</Button>
            <Button className="btn-primary" onClick={save} data-testid="plan-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Panel Facturas ----------------
function InvoicesPanel() {
  const [invoices, setInvoices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genOpen, setGenOpen] = useState(false);
  const [gen, setGen] = useState({ company_id: "", period_start: "", period_end: "", notes: "" });
  const [genLoading, setGenLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [inv, co] = await Promise.all([
        api.get("/billing/invoices"),
        api.get("/companies/").catch(() => ({ data: [] })),
      ]);
      setInvoices(inv.data || []); setCompanies(co.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!gen.company_id) { toast.error("Selecciona una empresa"); return; }
    setGenLoading(true);
    try {
      const payload = {
        company_id: gen.company_id,
        period_start: gen.period_start ? new Date(gen.period_start).toISOString() : null,
        period_end: gen.period_end ? new Date(gen.period_end).toISOString() : null,
        notes: gen.notes || null,
      };
      const { data } = await api.post("/billing/invoices/generate", payload);
      toast.success(`Factura ${data.invoice_number} generada`);
      setGenOpen(false);
      setGen({ company_id: "", period_start: "", period_end: "", notes: "" });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGenLoading(false); }
  };

  const download = async (inv) => {
    try {
      const r = await api.get(`/billing/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error descargando PDF"); }
  };

  const send = async (inv) => {
    if (!window.confirm(`¿Enviar factura ${inv.invoice_number} por email al administrativo de la empresa?`)) return;
    try {
      const { data } = await api.post(`/billing/invoices/${inv.id}/send`, {});
      toast.success(`Factura enviada a ${data.sent_to} (${data.mode})`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-stone-500">Genera facturas con el consumo real medido por período y envíalas por email.</div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
          <Button className="btn-primary" onClick={() => setGenOpen(true)} data-testid="new-invoice-btn"><Plus className="h-4 w-4 mr-1" /> Nueva factura</Button>
        </div>
      </div>
      <div className="card-elevated overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-stone-50/60">
            <TableHead>N°</TableHead><TableHead>Empresa</TableHead>
            <TableHead>Período</TableHead><TableHead>Total</TableHead>
            <TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></TableCell></TableRow>
            ) : invoices.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-stone-500">Sin facturas emitidas.</TableCell></TableRow>
            ) : (
              invoices.map((inv) => (
                <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                  <TableCell className="font-mono font-semibold">{inv.invoice_number}</TableCell>
                  <TableCell>{inv.company_name}</TableCell>
                  <TableCell className="text-xs">
                    {(inv.period_start || "").slice(0, 10)} → {(inv.period_end || "").slice(0, 10)}
                  </TableCell>
                  <TableCell className="font-mono">USD {Number(inv.total_usd).toFixed(2)}</TableCell>
                  <TableCell>
                    {inv.status === "sent" ? (
                      <Badge className="bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/30">Enviada</Badge>
                    ) : (
                      <Badge className="bg-stone-100 text-stone-700 border-stone-200">Borrador</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => download(inv)} data-testid={`invoice-pdf-${inv.id}`} title="Descargar PDF"><Download className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => send(inv)} data-testid={`invoice-send-${inv.id}`} title="Enviar por email" className="text-brand-emerald-dark"><Send className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent data-testid="new-invoice-dialog">
          <DialogHeader><DialogTitle>Generar factura</DialogTitle><DialogDescription>Se calculará el consumo real del período con las tarifas vigentes.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Empresa</Label>
              <Select value={gen.company_id} onValueChange={(v) => setGen({ ...gen, company_id: v })}>
                <SelectTrigger data-testid="gen-invoice-company"><SelectValue placeholder="Seleccionar empresa" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}{c.is_demo && " (DEMO)"}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Desde</Label><Input type="date" value={gen.period_start} onChange={(e) => setGen({ ...gen, period_start: e.target.value })} data-testid="gen-invoice-start" /></div>
              <div><Label>Hasta</Label><Input type="date" value={gen.period_end} onChange={(e) => setGen({ ...gen, period_end: e.target.value })} data-testid="gen-invoice-end" /></div>
            </div>
            <div><Label>Notas (opcional)</Label><Textarea rows={2} value={gen.notes} onChange={(e) => setGen({ ...gen, notes: e.target.value })} data-testid="gen-invoice-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)} disabled={genLoading}>Cancelar</Button>
            <Button className="btn-primary" onClick={generate} disabled={genLoading} data-testid="gen-invoice-submit">
              {genLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Generar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Panel Config (contador) ----------------
function ConfigPanel() {
  const [counter, setCounter] = useState(null);
  const [next, setNext] = useState(1);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/billing/counter");
    setCounter(data);
    setNext(data.next_number);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/billing/counter", { next_number: parseInt(next, 10) });
      toast.success("Contador actualizado");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!counter) return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>;
  return (
    <div className="card-elevated p-6 max-w-lg">
      <div className="font-serif-display text-2xl text-brand-ink mb-4">Contador correlativo de facturas</div>
      <p className="text-sm text-stone-500 mb-4">
        La próxima factura que emitas usará este número. Formato final: <code>FS-{new Date().getFullYear()}-XXXXX</code>.
      </p>
      <div className="space-y-1 mb-4">
        <Label>Próximo número</Label>
        <Input type="number" min="1" value={next} onChange={(e) => setNext(e.target.value)} data-testid="counter-next-number" />
      </div>
      <Button className="btn-primary" onClick={save} disabled={saving} data-testid="counter-save">
        {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Guardar
      </Button>
    </div>
  );
}
